import type { GuideQuestEvent } from "@yjsgames/guide-quest";
import { expandedModulePool } from "../data/moduleDefinitions.js";
import { REWARD_BUNDLES } from "../data/rewardData.js";
import { resolveRewards, type ResolvedReward } from "../systems/RewardResolver.js";
import type { ModuleInstance } from "../models/ModuleInstance.js";
import type { GameContext } from "./GameContext.js";
import type { RewardService } from "./commands/RewardService.js";
import type { ShellPorts } from "./commands/ports.js";

export interface GuideQuestFlowDeps {
  readonly ctx: GameContext;
  readonly ports: ShellPorts;
  readonly rewards: RewardService;
  readonly openRewardModal: (reward: ResolvedReward, instances?: readonly ModuleInstance[]) => void;
}

export class GuideQuestFlow {
  constructor(private readonly deps: GuideQuestFlowDeps) {}

  notify(event: GuideQuestEvent): void {
    if (!this.deps.ctx.guideQuest().ingest(event)) return;
    this.deps.ports.invalidateUi();
    this.deps.ports.persist();
  }

  notifyChapterReached(chapter: number): void {
    this.notify({ kind: "chapterReached", target: String(chapter), value: chapter });
  }

  notifyTabEntered(tab: string): void {
    this.notify({ kind: "tabEntered", target: tab });
  }

  notifyModulePlaced(definitionId: string): void {
    this.notify({ kind: "modulePlaced", target: definitionId });
  }

  notifyUpgradeBought(target = "any"): void {
    this.notify({ kind: "upgradeBought", target });
  }

  notifyActionCompleted(target: string): void {
    this.notify({ kind: "actionCompleted", target });
  }

  claimCurrentReward(): boolean {
    const claimed = this.deps.ctx.guideQuest().claimCurrent();
    if (!claimed) return false;

    const bundle = REWARD_BUNDLES[claimed.rewardBundleId];
    if (!bundle) {
      console.warn(`[GuideQuestFlow] missing reward bundle: ${claimed.rewardBundleId}`);
      this.deps.ports.invalidateUi();
      this.deps.ports.persistNow();
      return false;
    }

    const reward = resolveRewards(bundle, {
      rng: Math.random,
      rarityDefs: expandedModulePool(),
    });
    const instances = this.deps.rewards.grant(reward);
    this.deps.openRewardModal(reward, instances);
    this.syncActiveMilestones();
    this.notify({ kind: "rewardClaimed", target: "any" });
    this.deps.ports.invalidateUi();
    this.deps.ports.persistNow();
    return true;
  }

  /**
   * Milestone quests can become active after the player already passed the milestone.
   * Re-feed current durable state when claiming unlocks the next guide quest.
   */
  private syncActiveMilestones(): void {
    const active = this.deps.ctx.guideQuest().active();
    if (!active || active.completed) return;
    if (active.definition.objectiveKind === "chapterReached") {
      this.notifyChapterReached(this.deps.ctx.chapter().bestChapter);
    }
  }
}
